import { InteractionEvent, type VerificationCodeIdentifier } from '@logto/schemas';
import { useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useTimer } from 'react-timer-hook';

import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import { sendVerificationCode } from '@/apis/experience';
import { getInteractionEventFromState, userFlowToInteractionEventMap } from '@/apis/utils';
import useApi from '@/hooks/use-api';
import useErrorHandler from '@/hooks/use-error-handler';
import useToast from '@/hooks/use-toast';
import { UserFlow } from '@/types';
import { codeVerificationTypeMap } from '@/utils/sign-in-experience';
import { trackEvent } from '@/utils/analytics';
import { getOtpTimerExpiry, newOtpTimerExpiry, storeOtpTimerExpiry } from '@/utils/otp-timer';

export const timeRange = 59;

const useResendVerificationCode = (flow: UserFlow, identifier: VerificationCodeIdentifier) => {
  const { setToast } = useToast();
  const { state } = useLocation();
  const { t } = useTranslation();

  const interactionEvent = useMemo<InteractionEvent>(() => {
    if (flow === UserFlow.Continue) {
      const interactionEvent = getInteractionEventFromState(state);
      console.log('interactionEvent', interactionEvent);
      return interactionEvent ?? InteractionEvent.SignIn;
    }

    return userFlowToInteractionEventMap[flow];
  }, [flow, state]);

  // Restore the timer from session storage so the countdown persists if the user
  // navigated back to the sign-in page and returned without sending a new OTP.
  const { seconds, isRunning, restart } = useTimer({
    autoStart: true,
    expiryTimestamp: getOtpTimerExpiry(),
  });

  const handleError = useErrorHandler();
  const resendVerificationCode = useApi(sendVerificationCode);
  const { setVerificationId, setRefCode } = useContext(UserInteractionContext);

  const onResendVerificationCode = useCallback(async () => {
    const [error, result] = await resendVerificationCode(interactionEvent, identifier);

    if (error) {
      await handleError(error);

      return;
    }

    if (result) {
      // Renew the verification ID in the context
      setVerificationId(codeVerificationTypeMap[identifier.type], result.verificationId);
      setRefCode(result.referenceCode);
      setToast(t('description.passcode_sent'));
      const newExpiry = newOtpTimerExpiry();
      storeOtpTimerExpiry(newExpiry);
      restart(newExpiry, true);

      trackEvent(
        'verification_code_resend',
        'clicked',
        { eventId: 'verification_code_resend_clicked' },
        {
          userFlowType: flow,
          interactionEvent,
          identifierType: identifier.type,
        }
      );
    }
  }, [
    resendVerificationCode,
    interactionEvent,
    identifier,
    handleError,
    setVerificationId,
    setToast,
    restart,
  ]);

  return {
    seconds,
    isRunning,
    onResendVerificationCode,
  };
};

export default useResendVerificationCode;
